<template>
  <v-container
    :id="`product-infos-${product.id}`"
    class="pa-0"
  >
    <v-layout>
      <v-flex class="alignment mb-3">
        <h5 class="product__reference mt-1">
          {{ product.reference || (getSkuSelected && getSkuSelected.reference) || "" }}
        </h5>
        <h5 class="product__reference mt-1">
          Cód. int. {{ (getSkuSelected && getSkuSelected.sku_id_erp) || "" }}
        </h5>
        <h4 class="text-uppercase product__name mt-2">
          {{ product.descricaoCurta }}
        </h4>
        <h5
          v-if="productHasPrice"
          class="product__price mt-2"
        >
          <span
            v-if="product.oldPrice || getSkuSelected.oldPrice || currentPrice.old_price"
            class="product__price__old mb-2"
          >
            {{ product.oldPrice || getSkuSelected.oldPrice || currentPrice.old_price | currency }}
          </span>
          <span
            v-if="product.skus"
            :style="{ color: hasSAC ? '#FF5146' : '#0C4439' }"
          >
            {{ product.price || currentPrice.price | currency }}
          </span>
        </h5>
      </v-flex>
    </v-layout>

    <section
      style="position: relative"
      class="my-10"
    >
      <div
        v-if="hasSAC && getStoreSkuPrice('old')"
        class="product__sac-discount"
      >
        {{ getProductDiscount }}
      </div>
      <Carousel :images="getProductImages" />
    </section>

    <v-flex
      v-if="hasSAC || isPreSale"
      xs12
    >
      <div
        :style="isPreSale ?
          'background: #FFC56F; color: #000;'
          :
          'background: #FF5146'"
        class="product__sac-tag mx-3 mb-4"
      >
        {{ isPreSale ? 'Pré-venda' : sacTag }}
      </div>
    </v-flex>
    <v-divider class="product__divider" />

    <v-container
      v-if="hasOptions && getCustomer.salesAssistant"
      class="px-0"
    >
      <v-layout
        v-if="getProductColors.length"
        column
        align-start
      >
        <span class="product__option__title mt-2">
          Cor:
        </span>
        <div class="product__option__wrapper">
          <v-btn
            v-for="(color, idx) in getProductColors"
            :key="`${idx}-${color}`"
            depressed
            dark
            color="#140C22"
            :outline="selectedOptions.color !== color"
            class="product__option ma-0 mr-3 mt-2"
            @click="selectOption('color', color)"
          >
            {{ color }}
          </v-btn>
        </div>
      </v-layout>

      <v-layout
        v-if="getProductSizes.length"
        column
        align-start
      >
        <span class="product__option__title mt-2">
          Tamanho:
        </span>
        <div class="product__option__wrapper">
          <v-btn
            v-for="(size, idx) in getProductSizes"
            :key="`${idx}-${size}`"
            depressed
            dark
            color="#140C22"
            :outline="selectedOptions.size !== size"
            class="product__option ma-0 mr-3 mt-2"
            @click="selectOption('size', size)"
          >
            {{ size }}
          </v-btn>
        </div>
      </v-layout>

      <v-layout
        v-if="getProductVoltages.length"
        column
        align-start
      >
        <span class="product__option__title mt-2">
          Voltagem:
        </span>
        <div class="product__option__wrapper">
          <v-btn
            v-for="(voltage, idx) in getProductVoltages"
            :key="`${idx}-${voltage}`"
            depressed
            color="#140C22"
            dark
            :outline="selectedOptions.voltage !== voltage"
            class="product__option ma-0 mr-3 mt-2"
            @click="selectOption('voltage', voltage)"
          >
            {{ voltage }}
          </v-btn>
        </div>
      </v-layout>
    </v-container>

    <v-divider
      v-if="hasOptions & getCustomer.salesAssistant"
      class="product__divider"
    />

    <ProductDetailsTabs
      v-if="getSkuSelected"
      :product="product"
      :sku-selected="getSkuSelected"
      :current-price="currentPrice"
      :store="store"
      :shrink-percentage="shrinkPercentage"
      @change-quantity="onChangeQuantity"
    />
  </v-container>
</template>

<script>
/* eslint-disable camelcase */
import { mapGetters } from 'vuex';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { EventBus } from '@/event-bus.js';
import ProductDetailsTabs from './ProductDetailsTabs.vue';
import Carousel from '../Carousel.vue';
import { sacTags } from '../../utils/enum';

export default {
  components: {
    ProductDetailsTabs,
    Carousel,
  },

  props: {
    product: {
      type: Object,
      default: () => {},
    },
    store: {
      type: Object,
      default: () => {},
    },
    shrinkPercentage: {
      type: Number,
      default: 1,
    },
    needConfirmation: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    selectedOptions: {
      voltage: '',
      color: '',
      size: '',
    },
    discountPrice: 0,
  }),

  computed: {
    ...mapGetters(['getStore', 'getCustomer']),

    getProductVoltages() {
      const voltages = this.product.skus.length
        ? this.product.skus.filter((sku) => sku.color === this.selectedOptions.color && sku.voltage).map((sku) => sku.voltage)
        : [];
      return _.uniq(voltages);
    },

    getProductColors() {
      const colors = this.product.skus.filter((sku) => sku.color).map((sku) => sku.color);

      return colors.length ? _.uniq(colors) : [];
    },

    getProductSizes() {
      const sizes = this.product.skus.length
        ? this.product.skus.filter((sku) => sku.color === this.selectedOptions.color && sku.size).map((sku) => sku.size)
        : [];

      return _.uniq(sizes);
    },

    getProductImages() {
      const images = [];

      this.product.skus.forEach((sku) => sku.sku_images.forEach((item) => images.push(item.image)));

      return images;
    },

    hasOptions() {
      return this.getProductVoltages.length
        || this.getProductColors.length
        || this.getProductSizes.length;
    },

    getProductDiscount() {
      const percentage = Math.round(
        (1 - (this.getStoreSkuPrice('regular') / this.getStoreSkuPrice('old'))) * 100,
      );

      return `-${percentage}%`;
    },

    hasSAC() {
      return this.getSkuSelected
        ? this.getSkuSelected.product_type_id < 4
        : this.product.skus[0].product_type_id < 4;
    },

    // Real number to be determined by the brand
    isPreSale() {
      return this.getSkuSelected
        ? this.getSkuSelected.product_type_id === 12
        : this.product.skus[0].product_type_id === 12;
    },

    sacTag() {
      return this.getSkuSelected
        ? sacTags[this.getSkuSelected.product_type_id]
        : sacTags[this.product.skus[0].product_type_id];
    },

    getSelectedOptionVoltage() {
      return this.selectedOptions.voltage;
    },

    getSelectedOptionColor() {
      return this.selectedOptions.color;
    },

    getSelectedOptionSize() {
      return this.selectedOptions.size;
    },

    getSkuSelected() {
      if (this.getProductVoltages.length && this.getProductSizes.length) {
        return this.product.skus.find((sku) => sku.color === this.getSelectedOptionColor && sku.voltage === this.getSelectedOptionVoltage && sku.size === this.getSelectedOptionSize);
      }

      if (this.getProductVoltages.length && !this.getProductSizes.length) {
        return this.product.skus.find((sku) => sku.color === this.getSelectedOptionColor && sku.voltage === this.getSelectedOptionVoltage);
      }

      if (!this.getProductVoltages.length && this.getProductSizes.length) {
        return this.product.skus.find((sku) => sku.color === this.getSelectedOptionColor && sku.size === this.getSelectedOptionSize);
      }

      return this.product.skus.find((sku) => sku.color === this.getSelectedOptionColor);
    },

    currentPrice() {
      if (this.product && this.product.store_id && this.getSkuSelected) {
        return (this.getSkuSelected.sku_prices && this.store) && this.getSkuSelected.sku_prices.find((price) => price.store_id === this.product.store_id);
      }

      return (this.getSkuSelected && this.getSkuSelected.sku_prices && this.store) && this.getSkuSelected.sku_prices.find((price) => price.store_id === this.store.id);
    },

    getHighestPrice() {
      let maxSkuPrice = null;
      let maxPrice = -Infinity;

      this.product.skus.forEach((sku) => {
        const { price } = sku.sku_prices[0];

        if (price !== undefined && price > maxPrice) {
          maxPrice = price;
          maxSkuPrice = sku;
        }
      });

      return maxSkuPrice;
    },

    productHasPrice() {
      if ((this.getSkuSelected && this.getSkuSelected.price) || (this.getSkuSelected && this.getSkuSelected.sku_prices && this.getSkuSelected.sku_prices.length && this.currentPrice.price)) {
        return true;
      }
      return false;
    },
  },

  watch: {
    product() {
      this.loadProductInfos();
      this.changeProductOptions();
    },

    getSelectedOptionColor() {
      if (this.getProductSizes.length) {
        this.selectOption('size', this.getProductSizes.find((size) => size));
      }
      if (this.getProductVoltages.length) {
        this.selectOption('voltage', this.getProductVoltages.find((voltage) => voltage));
      }
    },

    discountPrice(value) {
      // eslint-disable-next-line vue/no-mutating-props
      this.product.oldPrice = this.product.price || this.currentPrice.price;
      // eslint-disable-next-line vue/no-mutating-props
      this.product.price = value;
      // eslint-disable-next-line vue/no-mutating-props
      this.product.hasDiscount = true;
    },
  },

  mounted() {
    this.loadProductInfos();

    EventBus.$on('addDiscount', (discountPrice) => {
      this.discountPrice = discountPrice;
    });
  },

  methods: {
    loadProductInfos() {
      const root = document.querySelector(`#product-infos-${this.product.id}`);
      root.style.setProperty('--shrink-percentage', this.shrinkPercentage);

      if (this.$route.query.skuId) {
        const skuSelected = this.product.skus.find((sku) => `${sku.id}` === `${this.$route.query.skuId}`);

        if (!this.selectedOptions.color && this.getProductColors.length === 1) {
          // eslint-disable-next-line prefer-destructuring
          this.selectedOptions.color = this.getProductColors[0];
        } else {
          this.selectedOptions.size = skuSelected.size;
          this.selectedOptions.color = skuSelected.color;
          this.selectedOptions.voltage = skuSelected.voltage;
        }
      } else if (this.product && this.product.size) {
        this.selectedOptions.color = this.product.color;
        if (this.product.voltage) {
          this.selectedOptions.voltage = this.product.voltage;
        }
        if (this.product.size) {
          this.selectedOptions.size = this.product.size;
        }
      } else {
        this.selectedOptions.color = this.getHighestPrice.color;
        if (this.getHighestPrice.voltage) {
          this.selectedOptions.voltage = this.getHighestPrice.voltage;
        }
        this.selectedOptions.size = this.getProductSizes.find((size) => size);
      }
    },

    changeProductOptions() {
      Object.entries(this.selectedOptions).forEach(([option, value]) => {
        this.$set(this.product, option, value);
      });
    },

    selectOption(option, value) {
      this.selectedOptions[option] = value;

      if (!this.needConfirmation) this.changeProductOptions();
    },

    getStoreSkuPrice(priceType) {
      const { old_price, price } = this.getSkuSelected ? this.currentPrice : this.getHighestPrice.sku_prices[0];

      const prices = {
        old: (old_price > price) ? old_price : null,
        regular: price,
      };

      return prices[priceType];
    },

    onChangeQuantity(quantity) {
      this.$emit('change-quantity', quantity);
    },
  },
};
</script>
<style scoped lang="scss">

%sac-information {
  background: #FF5146;
  border-radius: calc(4px * var(--shrink-percentage));
  font-size: calc(16px * var(--shrink-percentage));
  line-height: 102%;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #FFFFFF;
}

.v-carousel {
  -webkit-box-shadow: 0px 0px 0px 0px rgb(0 0 0 / 20%), 0px 0px 0px 0px rgb(0 0 0 / 14%), 0px 0px 0px 0px rgb(0 0 0 / 12%);
  box-shadow: 0px 0px 0px 0px rgb(0 0 0 / 20%), 0px 0px 0px 0px rgb(0 0 0 / 14%), 0px 0px 0px 0px rgb(0 0 0 / 12%);
}

.alignment {
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  text-align: start;
}

.imageResize {
  height: 100%;
  width: 100%;
}

.product {
  font-family: 'Nunito Sans';
  &__reference {
    font-size: calc(14px * var(--shrink-percentage));
    line-height: calc(18px * var(--shrink-percentage));
    font-weight: 600;
    color: #433D4EBF;
  }
  &__name {
    font-size: calc(20px * var(--shrink-percentage));
    line-height: calc(27px * var(--shrink-percentage));
    text-transform: uppercase;
    font-weight: 600;
    color: #433D4E;
  }
  &__price {
    display: flex;
    flex-flow: column wrap;
    font-weight: 700;
    font-size: calc(20px * var(--shrink-percentage));
    line-height: calc(18px * var(--shrink-percentage));
    text-transform: uppercase;
    &__old {
      font-size: calc(14px * var(--shrink-percentage));
      text-decoration-line: line-through;
      color: #AFAFAF;
    }
  }
  &__divider {
    margin: 0 !important;
    border-top: 1px solid rgba(0, 0, 0, 0.16)
  }
  &__option {
    min-width: 0;
    height: fit-content;
    padding: calc(10px * var(--shrink-percentage)) calc(18px * var(--shrink-percentage));
    border-radius: calc(4px * var(--shrink-percentage));
    font-weight: 600;
    font-size: calc(14px * var(--shrink-percentage));
    line-height: calc(19px * var(--shrink-percentage));
    &__title {
      font-weight: 400;
      font-size: calc(18px * var(--shrink-percentage));
      line-height: calc(25px * var(--shrink-percentage));
    }
    &__disabled {
      color: rgba(0, 0, 0, 0.36);
      border: 1px solid rgba(0, 0, 0, 0.36) !important;
      pointer-events: none;
    }
    &__wrapper {
      display: flex;
      flex-flow: row wrap;
    }
  }
  &__sac-discount {
    @extend %sac-information;
    position: absolute;
    z-index: 10;
    top: calc(45px * var(--shrink-percentage));
    left: calc(16px * var(--shrink-percentage));
    padding: calc(8px * var(--shrink-percentage)) calc(9px * var(--shrink-percentage));
    height: calc(32px * var(--shrink-percentage));
  }
  &__sac-tag {
    @extend %sac-information;
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(36px * var(--shrink-percentage));
  }
}

::v-deep .v-responsive__content {
  max-width: 100% !important;
}

</style>
